import { useState, useEffect, useRef, useCallback } from "react";
import apiFetch from "./apiFetch";
import { resultCache, inflightMap, getCacheTTL } from "./cache";
import useAuth from "../context/useAuth";
import appToast from "../components/appToast";

function buildFinalUrl(base, p) {
  if (!p || typeof p !== "object" || Object.keys(p).length === 0) return base;
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(p).filter(([, v]) => v != null)),
  ).toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * useQuery — fetches once per unique cacheKey, deduplicates concurrent
 * requests, and never aborts on component cleanup.
 */
export function useQuery(
  url,
  { skip = false, params = null, transform = null } = {},
) {
  const { clearUser } = useAuth();

  const newParamsStr = params != null ? JSON.stringify(params) : "null";
  const stableParamsStr = useRef(newParamsStr);
  if (stableParamsStr.current !== newParamsStr) {
    stableParamsStr.current = newParamsStr;
  }
  const paramsStr = stableParamsStr.current;
  const cacheKey = `${url}||${paramsStr}`;

  // Seed from cache immediately so components get data without a loading flash
  const cachedRaw =
    !skip && resultCache.has(cacheKey) ? resultCache.get(cacheKey) : undefined;
  const initialData =
    cachedRaw !== undefined
      ? transform
        ? transform(cachedRaw)
        : cachedRaw
      : undefined;

  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(!skip && cachedRaw === undefined);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  // Keep latest callbacks in refs — never in effect deps
  const clearUserRef = useRef(clearUser);
  const transformRef = useRef(transform);
  const paramsRef = useRef(params);
  const urlRef = useRef(url);
  clearUserRef.current = clearUser;
  transformRef.current = transform;
  paramsRef.current = params;
  urlRef.current = url;

  // Tracks whether this instance is still mounted
  const mountedRef = useRef(true);
  const skipRef = useRef(skip);
  skipRef.current = skip;

  const doFetch = useCallback(async () => {
    if (skipRef.current) return;

    // Return cached result immediately
    if (resultCache.has(cacheKey)) {
      const raw = resultCache.get(cacheKey);
      const fn = transformRef.current;
      if (mountedRef.current) {
        setData(fn ? fn(raw) : raw);
        setIsLoading(false);
      }
      return;
    }

    // Deduplicate: reuse an existing in-flight promise
    if (!inflightMap.has(cacheKey)) {
      const finalUrl = buildFinalUrl(urlRef.current, paramsRef.current);
      const promise = apiFetch(finalUrl, {}, clearUserRef.current)
        .then(async (res) => {
          if (!res) throw new Error("Logged out");
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw errData;
          }
          const json = await res.json();
          const ttl = getCacheTTL(urlRef.current);
          resultCache.set(cacheKey, json, ttl); // store with TTL
          return json;
        })
        .finally(() => {
          inflightMap.delete(cacheKey);
        });
      inflightMap.set(cacheKey, promise);
    }

    if (mountedRef.current) {
      setIsLoading(true);
      setIsError(false);
      setError(null);
    }

    try {
      const json = await inflightMap.get(cacheKey);
      const fn = transformRef.current;
      if (mountedRef.current) setData(fn ? fn(json) : json);
    } catch (err) {
      if (mountedRef.current) {
        setIsError(true);
        setError(err);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [cacheKey]);

  useEffect(() => {
    mountedRef.current = true;
    if (!skip) doFetch();
    else setIsLoading(false);
    return () => {
      mountedRef.current = false; // unsubscribe — never abort shared request
    };
  }, [cacheKey, skip]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when cache invalidation matches our url
  useEffect(() => {
    if (skip) return undefined;
    const unsubscribe = resultCache.subscribe((pattern) => {
      if (!pattern || urlRef.current.includes(pattern)) {
        inflightMap.delete(cacheKey);
        doFetch();
      }
    });
    return unsubscribe;
  }, [cacheKey, skip, doFetch]);

  const refetch = useCallback(() => {
    resultCache.delete(cacheKey);
    inflightMap.delete(cacheKey);
    doFetch();
  }, [cacheKey, doFetch]);

  return { data, isLoading, isError, error, refetch };
}

/**
 * useMutation — mirrors RTK Query's useXxxMutation hook behaviour.
 * Returns [triggerFn, { isLoading, isError, error, isSuccess, data }]
 */
export function useMutation(
  url,
  {
    method = "POST",
    buildUrl: buildUrlFn = null,
    transformBody = null,
    successMsg = null,
    errorMsg = null,
    silent = false,
    invalidates = [],
  } = {},
) {
  const { clearUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [data, setData] = useState(undefined);

  const trigger = useCallback(
    async (body) => {
      setIsLoading(true);
      setIsError(false);
      setError(null);
      setIsSuccess(false);
      setData(undefined);

      const finalUrl = buildUrlFn ? buildUrlFn(body) : url;
      const sendBody = transformBody ? transformBody(body) : body;
      const isFormData = sendBody instanceof FormData;

      try {
        const res = await apiFetch(
          finalUrl,
          {
            method,
            body: isFormData
              ? sendBody
              : sendBody !== undefined
                ? JSON.stringify(sendBody)
                : undefined,
          },
          clearUser,
        );
        if (!res) return;
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          const err = new Error(errData?.message || "Request failed");
          err.data = errData;
          err.status = res.status;
          setIsError(true);
          setError(err);
          throw err;
        }
        const json = await res.json().catch(() => ({}));
        setData(json);
        setIsSuccess(true);
        if (invalidates.length > 0) {
          invalidates.forEach((pattern) => resultCache.invalidate(pattern));
        }
        if (!silent) {
          const msg =
            successMsg || json?.message || "Action completed successfully.";
          appToast.success(msg);
        }
        return json;
      } catch (err) {
        setIsError(true);
        setError(err);
        if (!silent) {
          const msg =
            errorMsg ||
            err?.data?.message ||
            err?.message ||
            "Something went wrong.";
          appToast.error(msg);
        }
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, method, silent, successMsg, errorMsg],
  );

  /**
   * Provide .unwrap() on the trigger so code using
   * `await mutate(data).unwrap()` continues to work.
   */
  const triggerWithUnwrap = useCallback(
    (body) => {
      const promise = trigger(body);
      promise.unwrap = () => promise;
      return promise;
    },
    [trigger],
  );

  return [triggerWithUnwrap, { isLoading, isError, error, isSuccess, data }];
}
